import React, { useEffect, useState } from 'react';
import { Check, Sparkles, ArrowLeft, Star, Zap } from 'lucide-react';
import { useLocation } from 'wouter';
import { useBilling } from '../BillingContext';

interface Plan {
  priceId: string;
  productName: string;
  interval: 'month' | 'year';
  unitAmount: number;
  isRecommended: boolean;
  savingsLabel?: string;
  displayLabel: string;
}

const FEATURES = [
  'Unlimited lesson plans',
  'Activity sheets synchronized to every lesson',
  'Presentation decks in one click',
  'Aligned assessments for every lesson',
  'Curriculum Hub — align to your pacing guide',
  'All 12 resource themes',
  'K–8 Math, ELA, Science & Social Studies standards',
  'Save, organize & reuse all resources',
  'Priority AI generation',
];

function formatAmount(cents: number, interval: 'month' | 'year'): string {
  const dollars = (cents / 100).toFixed(2).replace('.00', '');
  return `$${dollars}/${interval === 'month' ? 'month' : 'year'}`;
}

export function UpgradePage() {
  const [, setLocation] = useLocation();
  const { billing, trialDaysLeft, startCheckout } = useBilling();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInterval, setSelectedInterval] = useState<'monthly' | 'annual' | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Checkout cancellation return handler — fires once on mount.
   *
   * When Stripe redirects the checkout tab back to /upgrade?billing=cancelled
   * we: (a) broadcast 'billing-cancelled' (no billing state change — just UX),
   * (b) clean the URL, and (c) try to close this checkout tab so the teacher
   * lands back on their original tab.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') !== 'cancelled') return;
    window.history.replaceState({}, '', window.location.pathname);
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel('teachbud-billing');
      ch.postMessage({ type: 'billing-cancelled' });
      ch.close();
    }
    // Short delay so the browser doesn't close before the broadcast is sent
    const t = setTimeout(() => { try { window.close(); } catch { /* ignore */ } }, 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    fetch('/api/billing/plans')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.plans) return;
        const parsed: Plan[] = data.plans
          .filter((p: any) => p.unit_amount && p.recurring)
          .map((p: any) => {
            const recurring = typeof p.recurring === 'string' ? JSON.parse(p.recurring) : p.recurring;
            const metadata = typeof p.price_metadata === 'string'
              ? JSON.parse(p.price_metadata ?? '{}')
              : p.price_metadata ?? {};
            return {
              priceId: p.price_id,
              productName: p.product_name,
              interval: recurring?.interval ?? 'month',
              unitAmount: p.unit_amount,
              isRecommended: metadata.is_recommended === 'true',
              savingsLabel: metadata.savings_label ?? undefined,
              displayLabel: metadata.display_label ?? (recurring?.interval === 'year' ? 'Annual' : 'Monthly'),
            };
          })
          .sort((a: Plan, b: Plan) => a.unitAmount - b.unitAmount);
        setPlans(parsed);
        // Pre-select the recommended plan (annual), falling back to first plan
        const recommended = parsed.find(p => p.isRecommended) ?? parsed.find(p => p.interval === 'year') ?? parsed[0];
        if (recommended) setSelectedInterval(recommended.interval === 'year' ? 'annual' : 'monthly');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleCheckout() {
    if (!selectedInterval) return;
    setCheckingOut(true);
    setError(null);
    try {
      await startCheckout(selectedInterval);
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
      setCheckingOut(false);
    }
  }

  const isExpired = billing.status === 'expired';
  const isCancelled = billing.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary/5 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 p-6 border-b border-slate-100 bg-white/80 backdrop-blur-sm">
        <button
          onClick={() => setLocation('/dashboard')}
          className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-primary" />
          <span className="font-semibold text-slate-900">
            {isExpired ? 'Resubscribe to TeachBud' : 'Upgrade to TeachBud'}
          </span>
        </div>
      </div>

      <div className="flex-1 max-w-2xl mx-auto w-full px-6 py-10 space-y-8">
        {/* Hero */}
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-bold text-slate-900">
            {isExpired
              ? 'Welcome back — your resources are waiting'
              : isCancelled
                ? 'Reactivate your subscription'
                : 'Everything included. One price.'}
          </h1>
          <p className="text-slate-500 text-sm leading-relaxed max-w-md mx-auto">
            {isExpired
              ? 'All your saved lessons and resources will be restored immediately when you resubscribe.'
              : trialDaysLeft !== null && trialDaysLeft <= 3
                ? `Your trial ends ${trialDaysLeft === 0 ? 'today' : `in ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''}`}. Subscribe to keep creating.`
                : 'Unlimited lesson plans, activity sheets, presentations, and assessments. All standards included.'}
          </p>
        </div>

        {/* Plan selector */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center text-slate-400 py-8">
            Plans unavailable right now. Please try again shortly.
          </div>
        ) : (
          <div className="space-y-3">
            {plans.map(plan => {
              const planInterval: 'monthly' | 'annual' = plan.interval === 'year' ? 'annual' : 'monthly';
              const selected = selectedInterval === planInterval;
              return (
                <button
                  key={plan.priceId}
                  onClick={() => setSelectedInterval(planInterval)}
                  className={`w-full text-left p-5 rounded-2xl border-2 transition-all ${
                    selected
                      ? 'border-primary bg-primary/5 shadow-sm shadow-primary/10'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{plan.displayLabel}</span>
                        {plan.isRecommended && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-semibold">
                            <Star size={10} fill="currentColor" /> Recommended
                          </span>
                        )}
                      </div>
                      {plan.savingsLabel && (
                        <p className="text-sm text-emerald-600 font-medium">{plan.savingsLabel}</p>
                      )}
                      <p className="text-xs text-slate-400">
                        7-day free trial included · Cancel anytime
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl font-bold text-slate-900">
                        {formatAmount(plan.unitAmount, plan.interval)}
                      </p>
                      {plan.interval === 'year' && (
                        <p className="text-xs text-slate-400">
                          ${(plan.unitAmount / 100 / 12).toFixed(2)}/month billed annually
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* CTA */}
        {error && (
          <p className="text-sm text-red-600 text-center">{error}</p>
        )}
        <button
          onClick={handleCheckout}
          disabled={!selectedInterval || checkingOut || plans.length === 0}
          className="w-full h-12 rounded-2xl bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-primary/20"
        >
          {checkingOut ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Redirecting to checkout…
            </span>
          ) : (
            <>
              <Zap size={16} />
              {isExpired || isCancelled ? 'Resubscribe Now' : 'Start Free Trial'}
            </>
          )}
        </button>

        <p className="text-center text-xs text-slate-400">
          Secure checkout powered by Stripe · You won't be charged until your trial ends
        </p>

        {/* Features list */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-3">
          <p className="text-sm font-semibold text-slate-700">Everything included</p>
          <ul className="space-y-2.5">
            {FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                <Check size={15} className="flex-shrink-0 text-emerald-500 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Money-back note */}
        <p className="text-center text-xs text-slate-400 leading-relaxed">
          Your data is yours. Cancel anytime — access continues through your billing period and your resources are preserved for 12 months.
        </p>
      </div>
    </div>
  );
}
